// ─────────────────────────────────────────────────────────────────────────────
// src/hooks/useTabs.ts
// Hook de gestion des onglets avec persistance localStorage.
// Les onglets survivent à la déconnexion / reconnexion.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback, useEffect } from "react";
import type { Page } from "./AppBar";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Tab {
  id: string; // UUID unique de l'onglet
  page: Page; // quelle page afficher
  label: string; // libellé affiché dans la barre
  icon: string; // emoji / icône
  createdAt: number; // timestamp ms — pour trier
  pinned?: boolean; // onglet épinglé (optionnel, futur)
}

interface UseTabsReturn {
  tabs: Tab[];
  activeTabId: string | null;
  activeTab: Tab | null;
  openTab: (page: Page) => void;
  closeTab: (id: string) => void;
  activateTab: (id: string) => void;
  moveTab: (fromIndex: number, toIndex: number) => void;
  closeOtherTabs: (id: string) => void;
  closeAllTabs: () => void;
  duplicateTab: (id: string) => void;
}

// ── Page metadata ─────────────────────────────────────────────────────────────

/**
 * Libellé et icône de chaque page — source unique (barre de navigation, onglets, menu mobile).
 * \uFE0E force le rendu texte monochrome (sinon Windows dessine certains glyphes en emoji couleur).
 */
export const PAGE_META: Record<Page, { label: string; icon: string }> = {
  accueil: { label: "Accueil", icon: "⌂\uFE0E" },
  dashboard: { label: "Top N Dashboard", icon: "▦\uFE0E" },
  mdx: { label: "MDX Query Builder", icon: "⬡\uFE0E" },
  articles: { label: "Articles", icon: "▤\uFE0E" },
  magazin: { label: "Analyse Magazins", icon: "▣\uFE0E" },
  marques: { label: "Analyse Marques", icon: "◈\uFE0E" },
  ventes: { label: "Analyse Groupes", icon: "▥\uFE0E" },
  sousfamille: { label: "Analyse Sous-Famille", icon: "◇\uFE0E" },
  saison: { label: "Analyse Saison", icon: "⟐\uFE0E" },
  groupe: { label: "Analyse Groupe", icon: "⊞\uFE0E" },
  univers: { label: "Analyse Univers", icon: "◍\uFE0E" },
  taille: { label: "Analyse Taille", icon: "↕\uFE0E" },
  couleur: { label: "Analyse Couleur", icon: "◐\uFE0E" },
  "couleur-taille": { label: "Couleur × Taille", icon: "◑\uFE0E" },
  famille: { label: "Analyse Famille", icon: "◆\uFE0E" },
  vendeur: { label: "Analyse Vendeurs", icon: "◎\uFE0E" },
  profile: { label: "Mon Profil", icon: "○\uFE0E" },
  prevision_f: { label: "Prévision F", icon: "◔\uFE0E" },
  attribution_role: { label: "Utilisateurs", icon: "◉\uFE0E" },
  ai_query: { label: "IA Query Builder", icon: "✦\uFE0E" },
  ia_famille: { label: "IA Famille", icon: "✧\uFE0E" },
  ia_sous_famille: { label: "IA Sous-Famille", icon: "✧\uFE0E" },
};

// ── Storage keys ──────────────────────────────────────────────────────────────

const STORAGE_TABS_KEY = "nw_tabs_list";
const STORAGE_ACTIVE_KEY = "nw_tabs_active";

// ── Helpers ───────────────────────────────────────────────────────────────────

function generateId(): string {
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function loadTabsFromStorage(): Tab[] {
  try {
    const raw = localStorage.getItem(STORAGE_TABS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as Tab[];
  } catch {
    return [];
  }
}

function saveTabsToStorage(tabs: Tab[]): void {
  try {
    localStorage.setItem(STORAGE_TABS_KEY, JSON.stringify(tabs));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

function loadActiveFromStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_ACTIVE_KEY);
  } catch {
    return null;
  }
}

function saveActiveToStorage(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(STORAGE_ACTIVE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_ACTIVE_KEY);
    }
  } catch {}
}

// ── Default home tab (always present) ────────────────────────────────────────

function makeHomeTab(): Tab {
  return {
    id: "tab_home",
    page: "accueil",
    label: "Accueil",
    icon: "⌂",
    createdAt: 0,
    pinned: true,
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useTabs(): UseTabsReturn {
  const [tabs, setTabsRaw] = useState<Tab[]>(() => {
    const stored = loadTabsFromStorage();
    if (stored.length === 0) {
      const home = makeHomeTab();
      saveTabsToStorage([home]);
      return [home];
    }
    // Ensure home tab always exists as first
    const hasHome = stored.some((t) => t.id === "tab_home");
    if (!hasHome) {
      const home = makeHomeTab();
      return [home, ...stored];
    }
    return stored;
  });

  const [activeTabId, setActiveTabIdRaw] = useState<string | null>(() => {
    const stored = loadActiveFromStorage();
    const storedTabs = loadTabsFromStorage();
    // Validate that stored active id still exists
    if (stored && storedTabs.some((t) => t.id === stored)) return stored;
    return storedTabs[0]?.id ?? "tab_home";
  });

  // Sync to localStorage on every change
  const setTabs = useCallback((updater: Tab[] | ((prev: Tab[]) => Tab[])) => {
    setTabsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveTabsToStorage(next);
      return next;
    });
  }, []);

  const setActiveTabId = useCallback((id: string | null) => {
    setActiveTabIdRaw(id);
    saveActiveToStorage(id);
  }, []);

  // ── Open / navigate ──────────────────────────────────────────────────────
  const openTab = useCallback(
    (page: Page) => {
      const meta = PAGE_META[page] ?? { label: page, icon: "◉" };

      // Lire les tabs actuels HORS du updater
      const currentTabs = loadTabsFromStorage();
      const existing = currentTabs.find((t) => t.page === page);

      if (existing) {
        // Onglet déjà ouvert → juste l'activer
        setActiveTabId(existing.id);
        return;
      }

      // Créer un nouvel onglet
      const newTab: Tab = {
        id: generateId(),
        page,
        label: meta.label,
        icon: meta.icon,
        createdAt: Date.now(),
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTabId(newTab.id);
    },
    [setTabs, setActiveTabId],
  );

  // ── Close ────────────────────────────────────────────────────────────────
  const closeTab = useCallback(
    (id: string) => {
      setTabs((prev) => {
        // Cannot close pinned tabs (home)
        const tab = prev.find((t) => t.id === id);
        if (tab?.pinned) return prev;

        const next = prev.filter((t) => t.id !== id);
        if (next.length === 0) {
          const home = makeHomeTab();
          setActiveTabId(home.id);
          return [home];
        }

        // If closing the active tab, move to adjacent
        if (id === activeTabId) {
          const closedIndex = prev.findIndex((t) => t.id === id);
          const newActive = next[Math.min(closedIndex, next.length - 1)];
          setActiveTabId(newActive.id);
        }

        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setTabs, setActiveTabId, activeTabId],
  );

  // ── Activate ─────────────────────────────────────────────────────────────
  const activateTab = useCallback(
    (id: string) => {
      setActiveTabId(id);
    },
    [setActiveTabId],
  );

  // ── Reorder (drag & drop) ────────────────────────────────────────────────
  const moveTab = useCallback(
    (fromIndex: number, toIndex: number) => {
      setTabs((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    },
    [setTabs],
  );

  // ── Context-menu helpers ─────────────────────────────────────────────────
  const closeOtherTabs = useCallback(
    (id: string) => {
      setTabs((prev) => {
        const kept = prev.filter((t) => t.id === id || t.pinned);
        setActiveTabId(id);
        return kept;
      });
    },
    [setTabs, setActiveTabId],
  );

  const closeAllTabs = useCallback(() => {
    const home = makeHomeTab();
    setTabs([home]);
    setActiveTabId(home.id);
  }, [setTabs, setActiveTabId]);

  const duplicateTab = useCallback(
    (id: string) => {
      setTabs((prev) => {
        const tab = prev.find((t) => t.id === id);
        if (!tab) return prev;
        const newTab: Tab = {
          ...tab,
          id: generateId(),
          createdAt: Date.now(),
          pinned: false,
        };
        const idx = prev.findIndex((t) => t.id === id);
        const next = [...prev];
        next.splice(idx + 1, 0, newTab);
        setActiveTabId(newTab.id);
        return next;
      });
    },
    [setTabs, setActiveTabId],
  );

  const activeTab = tabs.find((t) => t.id === activeTabId) ?? tabs[0] ?? null;

  return {
    tabs,
    activeTabId,
    activeTab,
    openTab,
    closeTab,
    activateTab,
    moveTab,
    closeOtherTabs,
    closeAllTabs,
    duplicateTab,
  };
}
